import { z } from 'zod';

/** Supported locale values */
export const LOCALE_OPTIONS = [
  { value: 'ko', label: '한국어' },
  { value: 'en', label: 'English' },
] as const;

/** Common IANA timezone options */
export const TIMEZONE_OPTIONS = [
  { value: 'Asia/Seoul', label: 'Asia/Seoul (KST, UTC+9)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST, UTC+9)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST, UTC+8)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT, UTC+8)' },
  { value: 'UTC', label: 'UTC (UTC+0)' },
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST)' },
  { value: 'America/New_York', label: 'America/New_York (EST/EDT)' },
  { value: 'America/Chicago', label: 'America/Chicago (CST/CDT)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST/PDT)' },
] as const;

/** UpdateProfile zod schema — PATCH /api/eraf/me */
export const updateProfileSchema = z.object({
  displayName: z
    .string()
    .min(1, '표시명을 입력해주세요')
    .max(100, '표시명은 100자 이하여야 합니다')
    .trim(),
  preferredLocale: z
    .string()
    .min(1, '언어를 선택해주세요')
    .refine(
      (v) => LOCALE_OPTIONS.some((o) => o.value === v),
      '지원하지 않는 언어입니다'
    ),
  preferredTimezone: z
    .string()
    .min(1, '타임존을 선택해주세요'),
});

export type UpdateProfileFormValues = z.infer<typeof updateProfileSchema>;

/** PasswordChange zod schema — POST /api/eraf/me/password-change */
export const passwordChangeSchema = z
  .object({
    currentPassword: z
      .string()
      .min(1, '현재 비밀번호를 입력해주세요'),
    newPassword: z
      .string()
      .min(10, '비밀번호는 10자 이상이어야 합니다')
      .max(64, '비밀번호는 64자 이하여야 합니다')
      .regex(/[A-Z]/, '대문자를 하나 이상 포함해야 합니다')
      .regex(/[a-z]/, '소문자를 하나 이상 포함해야 합니다')
      .regex(/[0-9]/, '숫자를 하나 이상 포함해야 합니다')
      .regex(/[^A-Za-z0-9]/, '특수문자를 하나 이상 포함해야 합니다'),
    confirmPassword: z.string().min(1, '비밀번호 확인을 입력해주세요'),
  })
  .superRefine((data, ctx) => {
    if (data.newPassword !== data.confirmPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '새 비밀번호와 확인 비밀번호가 일치하지 않습니다',
        path: ['confirmPassword'],
      });
    }
    if (
      data.currentPassword &&
      data.newPassword &&
      data.currentPassword === data.newPassword
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '새 비밀번호는 현재 비밀번호와 달라야 합니다',
        path: ['newPassword'],
      });
    }
  });

export type PasswordChangeFormValues = z.infer<typeof passwordChangeSchema>;

/**
 * Compute password strength (0–4) based on:
 * 0 — empty
 * 1 — length < 10 or only one category
 * 2 — 2 categories
 * 3 — 3 categories
 * 4 — all 4 categories (upper + lower + digit + special) AND length ≥ 12
 */
export function computePasswordStrength(password: string): 0 | 1 | 2 | 3 | 4 {
  if (!password) return 0;
  let score = 0;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  if (password.length < 10) return score <= 1 ? 1 : (score <= 2 ? 1 : 2) as 0 | 1 | 2 | 3 | 4;
  if (score === 4 && password.length >= 12) return 4;
  if (score >= 3) return 3;
  if (score >= 2) return 2;
  return 1;
}
